import React, { useRef, useState } from 'react';
import { Avatar, Button, Tooltip } from 'antd';
import moment from 'moment';

import { getDateFromNow, getFormattedUTCDate } from 'utils/dateUtils';
import {
  AssignmentCommentViewModel,
  CustomerCommentViewModel,
  MemberCommentViewModel,
} from 'api/adminApiClient';
import { getAvatarContent, getFullName } from 'utils/personUtils';
import CommentInput from '../../CommentInput';
import { SubmitComment } from '../../CommentInput';

import style from './CommentCard.module.less';

type Comment =
  | CustomerCommentViewModel
  | MemberCommentViewModel
  | AssignmentCommentViewModel;

type Props = {
  comment: Comment;
  onSubmit: (data: SubmitComment) => Promise<boolean | undefined>;
};

export default function CommentPost(props: Props) {
  const { comment, onSubmit } = props;
  const [isEditMode, setIsEditMode] = useState(false);
  const {
    id,
    firstName,
    lastName,
    text,
    createdDate,
    updatedDate,
    updatedByLastName,
    updatedByFirstName,
  } = comment;
  const scrollRef = useRef<HTMLDivElement>(null);
  const parentRef = useRef<HTMLDivElement>(null);

  const momentCreatedDate = moment(createdDate);
  const momentUpdatedDate = moment(updatedDate);
  const areDatesEqual = momentCreatedDate.isSame(momentUpdatedDate);

  return (
    <>
      {isEditMode ? (
        <CommentInput
          onSubmit={onSubmit}
          comment={comment}
          scrollRef={scrollRef}
          parentRef={parentRef}
          onClose={() => setIsEditMode(false)}
          isEditMode={true}
        />
      ) : (
        <div className={style.card} data-id={id}>
          <div className={style.cardAvatar}>
            <Avatar size={24}>
              {getAvatarContent(firstName, lastName)}
            </Avatar>
          </div>

          <div className={style.cardContent}>
            <div className={style.cardTitle}>
              <div className={style.author}>{getFullName(firstName, lastName)}</div>

              {createdDate && (
                <div className={style.accent}>
                  {getDateFromNow(createdDate)}
                </div>
              )}

              {!areDatesEqual && updatedDate && (
                <Tooltip
                  className={style.accent}
                  title={`${getFullName(
                    updatedByFirstName,
                    updatedByLastName,
                  )} updated comment at ${getFormattedUTCDate(updatedDate)}`}
                  trigger="hover"
                >
                  Edited
                </Tooltip>
              )}

              <div>
                <Button onClick={() => setIsEditMode(true)} type='text' className={style.btn}>
                  <i className="icon icon-edit" />
                </Button>
              </div>
            </div>

            <div className={style.text}>{text}</div>
          </div>
        </div>
      )}
    </>
  );
}
